import type { DataGroup, TreeDataGridRow } from '@/widgets/TreeDataGrid';
import { DataGridViewType } from '@/features/DataGridTypeSwitcher';
import type { Filters } from '@/features/filters';
import { prepareFilters } from '@/features/filters';
import { prepareDateRangeValueObject } from '@/shared/api';
import { DEFAULT_PAGE_SIZE, RowHeights } from '@/shared/constants';
import { DataStatus, isFinished } from '@/shared/lib';
import type { AppThunk } from '@/shared/model';
import { createAppSlice } from '@/shared/model';
import { DateScale, DateRangePresets, Lifetime } from '@/shared/types';
import type {
  SerializedDateRangeValueObject,
  Sorting,
  SortingItem,
} from '@/shared/types';
import type { TotalData } from '@/shared/ui';
import * as CohortScannerAPI from '../api/cohortScannerAPI';
import {
  ValueType,
  type DataConfig,
  type DataResponse,
  type Highlight,
  type Meta,
  type TreeDataResponse,
} from './types';

interface State {
  meta:
  | {
    data: Meta;
    status: DataStatus.finished;
  }
  | {
    data: null;
    status: Exclude<DataStatus, DataStatus.finished>;
  };
  config: DataConfig | null;
}

const initialState: State = {
  meta: {
    status: DataStatus.idle,
    data: null,
  },
  config: null,
};

export const cohortScannerSlice = createAppSlice({
  name: 'cohortScanner',
  initialState,
  reducers: (create) => ({
    initMeta: create.asyncThunk(
      (_: void) => {
        return CohortScannerAPI.getMeta();
      },
      {
        pending: (state) => {
          state.meta.status = DataStatus.loading;
        },
        fulfilled: (state, action) => {
          state.meta.data = action.payload;
          state.meta.status = DataStatus.finished;
        },
        rejected: (state) => {
          state.meta.status = DataStatus.error;
          state.meta.data = null;
        },
      },
    ),
    updateConfig: create.reducer<DataConfig>((state, action) => {
      state.config = action.payload;
    }),
    updateDateScale: create.reducer<DateScale>((state, action) => {
      if (!state.config) {
        return;
      }

      state.config.dateScale = action.payload;
    }),
    updateDateRange: create.reducer<SerializedDateRangeValueObject>(
      (state, action) => {
        if (!state.config) {
          return;
        }

        state.config.dateRange = action.payload;
      },
    ),
    updateProbabilisticAttribution: create.reducer<boolean>((state, action) => {
      if (!state.config) {
        return;
      }

      state.config.probabilisticAttribution = action.payload;
    }),
    updateLifetime: create.reducer<Lifetime>((state, action) => {
      if (!state.config) {
        return;
      }

      state.config.lifetime = action.payload;
    }),
    updateDimensions: create.reducer<string[]>((state, action) => {
      if (!state.config) {
        return;
      }

      state.config.dimensions = action.payload;
    }),
    updateFilters: create.reducer<Filters>((state, action) => {
      if (!state.config) {
        return;
      }

      state.config.filters = action.payload;
    }),
    updateDataGridViewType: create.reducer<DataGridViewType>(
      (state, action) => {
        if (!state.config) {
          return;
        }

        state.config.dataGridViewType = action.payload;
      },
    ),
    updateMetric: create.reducer<string>((state, action) => {
      if (!state.config) {
        return;
      }

      state.config.metric = action.payload;
    }),
    updateSorting: create.reducer<{ column: string; item?: SortingItem }>(
      (state, action) => {
        if (!state.config) {
          return;
        }

        if (!action.payload.item) {
          state.config.sorting = state.config.sorting.filter((item) => {
            return item.name !== action.payload.column;
          });
        } else {
          const index = state.config.sorting.findIndex((item) => {
            return item.name === action.payload.column;
          });

          if (index !== -1) {
            state.config.sorting[index] = action.payload.item;
          } else {
            state.config.sorting.push(action.payload.item);
          }
        }
      },
    ),
    updateColumnsWidth: create.reducer<{ name: string; width?: number }[]>(
      (state, action) => {
        if (!state.config) {
          return;
        }

        state.config.columnsWidth = {
          ...state.config.columnsWidth,
          ...Object.fromEntries(
            action.payload.map((item) => {
              return [item.name, item.width];
            }),
          ),
        };
      },
    ),
    updateRowHeight: create.reducer<RowHeights>((state, action) => {
      if (!state.config) {
        return;
      }

      state.config.rowHeight = action.payload;
    }),
    updateHighlight: create.reducer<Highlight>((state, action) => {
      if (!state.config) {
        return;
      }

      state.config.highlight = action.payload;
    }),
    reorderDimensions: create.reducer<{
      id: string;
      oldIndex: number;
      newIndex: number;
    }>((state, action) => {
      if (!state.config) {
        return;
      }

      const newDimensions = state.config.dimensions.toSpliced(
        action.payload.oldIndex,
        1,
      );

      newDimensions.splice(action.payload.newIndex, 0, action.payload.id);
      state.config.dimensions = newDimensions;
    }),
    setupVendorDefault: create.reducer<{ lifetimeTwoYearsExtension: boolean }>(
      (state, action) => {
        if (!state.meta.data) {
          return;
        }

        state.config = {
          dateScale: DateScale.DAY,
          dateRange: { preset: DateRangePresets.LAST_30_DAYS },
          lifetime: action.payload.lifetimeTwoYearsExtension
            ? Lifetime.TWO_YEARS
            : Lifetime.YEAR,
          probabilisticAttribution: true,
          filters: [],
          dimensions: state.meta.data.defaultColumns.dimensions,
          metric: state.meta.data.defaultColumns.metric,
          highlight: {
            valueType: ValueType.absolute,
            levels: 5,
          },
          rowHeight: RowHeights.normal,
          dataGridViewType: DataGridViewType.dataTable,
          columnsWidth: {},
          sorting: [],
        };
      },
    ),
    reset: create.reducer(() => {
      return { ...initialState };
    }),
  }),
  selectors: {
    selectMetaIsLoaded: (state) => isFinished(state.meta.status),
    selectMetaStatus: (state) => state.meta.status,
    selectConfig: (state) => state.config,
    selectMetaDimensions: (state) => {
      return state.meta.data?.dimensions ?? [];
    },
    selectMetaMetrics: (state) => {
      return state.meta.data?.metrics ?? [];
    },
    selectMetaConfig: (state) => {
      return state.meta.data?.config ?? {};
    },
  },
});

interface GetPageParams {
  dimensions: string[];
  metric: string;
  dateScale: DateScale;
  dateRange: SerializedDateRangeValueObject;
  sorting: Sorting;
  probabilisticAttribution: boolean;
  lifetime: Lifetime;
  filters: Filters;
  offset?: number;
}

export const getPage =
  (params: GetPageParams): AppThunk<Promise<DataResponse>> =>
    (_dispatch, getState) => {
      const state = getState();
      const allDimensions = selectMetaDimensions(state);
      const allMetrics = selectMetaMetrics(state);
      const { dimensionsFilters, metricsFilters } = prepareFilters(
        params.filters,
        allMetrics,
        allDimensions,
      );

      return CohortScannerAPI.getData({
        dimensions: params.dimensions,
        metric: params.metric,
        dateScale: params.dateScale,
        dateRange: prepareDateRangeValueObject(params.dateRange),
        offset: params.offset || 0,
        sort: params.sorting,
        probabilisticAttribution: params.probabilisticAttribution,
        lifetime: params.lifetime,
        dimensionsFilters,
        metricsFilters,
        pageSize: DEFAULT_PAGE_SIZE,
      });
    };

export const getTreeData =
  (parentGroups: DataGroup[]): AppThunk<Promise<TreeDataResponse>> =>
    (_dispatch, getState) => {
      const state = getState();
      const {
        dimensions,
        metric,
        dateRange,
        dateScale,
        lifetime,
        filters,
        sorting,
        probabilisticAttribution,
      } = selectConfig(state)!;
      const allDimensions = selectMetaDimensions(state);
      const allMetrics = selectMetaMetrics(state);
      const { dimensionsFilters, metricsFilters } = prepareFilters(
        filters,
        allMetrics,
        allDimensions,
      );

      return CohortScannerAPI.getTreeData({
        dimensions: [dimensions[parentGroups.length]],
        metric,
        dateScale,
        dateRange: prepareDateRangeValueObject(dateRange),
        sort: sorting,
        probabilisticAttribution,
        lifetime,
        dimensionsFilters,
        metricsFilters,
        parentGroups: parentGroups,
      }).then((response) => {
        return {
          columns: response.columns,
          rows: response.rows.map((row) => {
            return {
              ...row,
              path: [
                ...parentGroups.map((parentGroup) => {
                  return parentGroup.value;
                }),
                row[dimensions[parentGroups.length]],
              ],
            } as TreeDataGridRow;
          }),
        };
      });
    };

interface GetTotalParams {
  dimensions: string[];
  metric: string;
  dateRange: SerializedDateRangeValueObject;
  dateScale: DateScale;
  lifetime: Lifetime;
  filters: Filters;
  probabilisticAttribution: boolean;
}

export const getTotalData =
  (params: GetTotalParams): AppThunk<Promise<TotalData>> =>
    (_dispatch, getState) => {
      const state = getState();
      const allDimensions = selectMetaDimensions(state);
      const allMetrics = selectMetaMetrics(state);
      const { dimensionsFilters } = prepareFilters(
        params.filters,
        allMetrics,
        allDimensions,
      );

      return CohortScannerAPI.getTotal({
        dimensions: params.dimensions,
        metric: params.metric,
        dateRange: prepareDateRangeValueObject(params.dateRange),
        dateScale: params.dateScale,
        probabilisticAttribution: params.probabilisticAttribution,
        lifetime: params.lifetime,
        dimensionsFilters,
      });
    };

interface GetMinMaxParams {
  dataGridViewType: DataGridViewType;
  dimensions: string[];
  metric: string;
  filters: Filters;
  dateRange: SerializedDateRangeValueObject;
  dateScale: DateScale;
  probabilisticAttribution: boolean;
  lifetime: Lifetime;
}

export const getMinMax =
  (params: GetMinMaxParams): AppThunk<Promise<{ min: number; max: number }>> =>
    (_dispatch, getState) => {
      const state = getState();
      const allDimensions = selectMetaDimensions(state);
      const allMetrics = selectMetaMetrics(state);
      const { dimensionsFilters, metricsFilters } = prepareFilters(
        params.filters,
        allMetrics,
        allDimensions,
      );

      return CohortScannerAPI.getMinMax({
        dimensions: params.dimensions,
        metric: params.metric,
        dateRange: prepareDateRangeValueObject(params.dateRange),
        dateScale: params.dateScale,
        lifetime: params.lifetime,
        probabilisticAttribution: params.probabilisticAttribution,
        metricsFilters,
        dimensionsFilters,
        groupedData: params.dataGridViewType === DataGridViewType.treeDataGrid,
      });
    };

export const {
  setupVendorDefault,
  updateConfig,
  reset,
  initMeta,
  updateFilters,
  updateDateScale,
  updateDateRange,
  updateProbabilisticAttribution,
  updateLifetime,
  updateDimensions,
  updateDataGridViewType,
  updateMetric,
  reorderDimensions,
  updateColumnsWidth,
  updateSorting,
  updateHighlight,
  updateRowHeight,
} = cohortScannerSlice.actions;
export const {
  selectMetaIsLoaded,
  selectConfig,
  selectMetaStatus,
  selectMetaDimensions,
  selectMetaMetrics,
  selectMetaConfig,
} = cohortScannerSlice.selectors;
